import { t } from 'i18next';

import { DataTableRow } from 'components/dataTable';
import { getDefaultDatePresets } from 'components/filters/date/keys';
import { AmountFilterPassedProps, CheckboxFilterPassedProps, DateFilterPassedProps } from 'components/filters/types';

import { CustodialWalletTransaction, CustodialWalletTransactionStatus, CustodialWalletTransactionType } from 'types/custodialWallet';
import { Filter, FilterSection, FilterType } from 'types/filters';

import { KEYS, LABELS } from './keys';
import { Filters } from './types';

export const isRefundMovement = (type: CustodialWalletTransactionType) =>
  type === CustodialWalletTransactionType.Refund ||
  type === CustodialWalletTransactionType.TransferFromRefundWallet;

export const makeFilterSections = (filterMap: Record<string, Filter<Filters>>): FilterSection[] => {
  return [
    {
      key: 'filters',
      content: [filterMap.types, filterMap.date, filterMap.statuses, filterMap.amount],
    },
  ];
};

interface TransactionCurrencyMetadata {
  settlementCurrencyCode: string;
}

export interface RefundAccountTransactionRow extends CustodialWalletTransaction, DataTableRow {
  id: string;
  sourceId: string | null;
  settlementCurrencyCode: string;
}

export const transformTransactionsListDataToTableFormat = (
  records: CustodialWalletTransaction[],
  currencyMetadata: TransactionCurrencyMetadata,
): RefundAccountTransactionRow[] => {
  const getSourceId = (record: CustodialWalletTransaction): string | null => {
    if (isRefundMovement(record.type)) {
      return record.refundUuid;
    }

    if (record.type === CustodialWalletTransactionType.MerchantTopup) {
      return record.topupUuid;
    }

    return record.chargeUuid;
  };

  return records
    ? records.map((accountRecordInfo) => ({
        ...accountRecordInfo,
        ...currencyMetadata,
        id: accountRecordInfo.uuid,
        sourceId: getSourceId(accountRecordInfo),
      }))
    : [];
};

export const makeFilters = (): Record<string, Filter<unknown>> => {
  const datePresets = getDefaultDatePresets();
  const statusFilter: Filter<CheckboxFilterPassedProps> = {
    key: KEYS.FILTERS.STATUSES,
    label: t(LABELS.FILTERS.STATUS.LABEL),
    type: FilterType.Checkbox,
    options: [
      {
        key: CustodialWalletTransactionStatus.Failed,
        value: CustodialWalletTransactionStatus.Failed,
        label: t(LABELS.FILTERS.STATUSES[CustodialWalletTransactionStatus.Failed]),
      },
      {
        key: CustodialWalletTransactionStatus.Submitted,
        value: CustodialWalletTransactionStatus.Submitted,
        label: t(LABELS.FILTERS.STATUSES.SUBMITTED),
      },
      {
        key: CustodialWalletTransactionStatus.Completed,
        value: CustodialWalletTransactionStatus.Completed,
        label: t(LABELS.FILTERS.STATUSES[CustodialWalletTransactionStatus.Completed]),
      },
    ],
  };

  const dateFilter: Filter<DateFilterPassedProps> = {
    key: 'date',
    label: t(LABELS.FILTERS.DATE.LABEL),
    type: FilterType.Date,
    presets: datePresets,
  };

  const settlementAmountFilter: Filter<AmountFilterPassedProps> = {
    key: 'amount',
    type: FilterType.Amount,
    label: t(LABELS.FILTERS.SETTLEMENT_AMOUNT.LABEL),
  };

  const typesFilter: Filter<CheckboxFilterPassedProps> = {
    key: 'types',
    label: t(LABELS.FILTERS.TYPES.LABEL),
    type: FilterType.Checkbox,
    options: [
      {
        key: CustodialWalletTransactionType.MerchantTopup,
        value: CustodialWalletTransactionType.MerchantTopup,
        label: t(LABELS.FILTERS.TYPES[CustodialWalletTransactionType.MerchantTopup]),
      },
      {
        key: CustodialWalletTransactionType.Refund,
        value: CustodialWalletTransactionType.Refund,
        label: t(LABELS.FILTERS.TYPES[CustodialWalletTransactionType.Refund]),
      },
    ],
  };

  return [typesFilter, dateFilter, statusFilter, settlementAmountFilter].reduce((acc, cur) => {
    return { ...acc, [cur.key]: cur };
  }, {});
};

export const prepareFilters = (filters: Filters): Filters | undefined => {
  if (!filters) {
    return undefined;
  }

  const { date, types } = filters;

  const nextFilters: Filters = {};
  if (date) {
    nextFilters.date = date;
  }

  if (types?.includes(CustodialWalletTransactionType.Refund)) {
    nextFilters.types = [
      ...new Set([...types, CustodialWalletTransactionType.TransferFromRefundWallet]),
    ];
  }

  return { ...filters, ...nextFilters };
};
